import { FC, useContext, useEffect, useState } from 'react';

import { H3, Separator, Toggle, Tooltip } from '@smartshell/ui-kit';

import { SettingsLock, TimeOutInput, ScreenSaver } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import s from './ShellScreenSaver.module.scss';

export const ShellScreenSaver: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { screenSaver, screenSaverTimeout },
      isLoading,
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const isAvailable = useIsFeatureAvailable('shell_skin_screensaver');

  const [isShown, setIsShown] = useState(Boolean(screenSaver));

  useEffect(() => {
    setIsShown(Boolean(screenSaver));
  }, [screenSaver]);

  useEffect(() => {
    if (!isShown && screenSaver) {
      setSetting({ alias: ESettingsAliases.SHELL_SCREENSAVER, value: '' });
    }
  }, [isShown]);

  return (
    <div className={s.container}>
      <div className={s.fields}>
        <Toggle
          onChange={setIsShown}
          value={isShown}
          label={t('setting.shell.screensaver.title')}
          description={t(
            'settings.shell.customization.screen_saver.description'
          )}
          disabled={isLoading || !isAvailable}
          variant="secondary"
          emptyBg
          className={s.toggle}
          rightContent={
            isAvailable ? (
              <Tooltip
                contentClassName={s.tooltip_content}
                content={
                  <div className={s.tooltip_frame}>
                    <H3>{t('setting.shell.screensaver.info.static')}</H3>
                    <ul className={s.tooltip_list}>
                      <li className={s.tooltip_item}>
                        <span className={s.tooltip_text}>
                          {t('setting.shell.bg.optimal')}
                        </span>
                        <span className={s.tooltip_value}>1920х1080</span>
                      </li>
                      <li className={s.tooltip_item}>
                        <span className={s.tooltip_text}>
                          {t('application.image.maxSize')}
                        </span>
                        <span className={s.tooltip_value}>
                          7 {t('setting.shell.bg.optimal.size')}
                        </span>
                      </li>
                      <li className={s.tooltip_item}>
                        <span className={s.tooltip_text}>
                          {t('application.image.format')}
                        </span>
                        <span className={s.tooltip_value}>
                          .jpg, .jpeg .png
                        </span>
                      </li>
                    </ul>
                    <Separator />
                    <H3>{t('setting.shell.screensaver.info.dynamic')}</H3>
                    <ul className={s.tooltip_list}>
                      <li className={s.tooltip_item}>
                        <span className={s.tooltip_text}>
                          {t('setting.shell.bg.maximal')}
                        </span>
                        <span className={s.tooltip_value}>1920х1080</span>
                      </li>
                      <li className={s.tooltip_item}>
                        <span className={s.tooltip_text}>
                          {t('application.image.maxSize')}
                        </span>
                        <span className={s.tooltip_value}>
                          30 {t('setting.shell.bg.optimal.size')}
                        </span>
                      </li>
                      <li className={s.tooltip_item}>
                        <span className={s.tooltip_text}>
                          {t('application.image.format')}
                        </span>
                        <span className={s.tooltip_value}>.mp4, .webm</span>
                      </li>
                    </ul>
                  </div>
                }
              />
            ) : (
              <SettingsLock />
            )
          }
        />
        <Separator style={{ margin: '16px 0' }} />
        <TimeOutInput
          label={t('setting.shell.screensaver.headroom')}
          setting={screenSaverTimeout}
          updateSetting={setSetting}
          settingAlias={ESettingsAliases.SHELL_SCREENSAVER_TIMEOUT}
          inMinutes
          maxMinutes={60}
          disabled={isShown}
        />
      </div>
      <ScreenSaver disabled={!isAvailable || !isShown} />
    </div>
  );
};
